import React, { useEffect, useMemo } from 'react'
import Styles from './SEOScoreGauge.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useSEOAnalysis } from '../hooks/useSEOAnalysis'
import { SEOAnalysisError } from '../SEOAnalysis'
import { Result } from '@almanaclabs/yoastseo/dist/index.js'
import { useRootStore } from 'stores/RootStore'
import { CONTENT_CHANNELS } from 'components/Wizards/WizardsV3/constants'
import { TextChannelKey } from 'types'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useDebouncedContentAnalysis } from '../hooks/useDebouncedContentAnalysis'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const DIAMETER = 234
const RADIUS = DIAMETER / 2

const GAUGE_X_OFFSET = 18
const GAUGE_Y_OFFSET = 17

const START_ANGLE = 12
const END_ANGLE = 168

const RED = '#EC1E28'
const YELLOW = '#F3D73B'
const GREEN = '#04AF00'

const calculateSemicirclePoint = (value: number): { x: number; y: number } => {
  if (value < 0 || value > 100) {
    throw new Error('Value must be between 0 and 100.')
  }

  const startRad = (START_ANGLE * Math.PI) / 180
  const endRad = (END_ANGLE * Math.PI) / 180

  const theta = endRad - (value / 100) * (endRad - startRad)

  const x = RADIUS + RADIUS * Math.cos(theta)
  const y = RADIUS - RADIUS * Math.sin(theta)

  return { x, y }
}

const calculateYoastTotals = (data: Result | null): { score: number; issueCount: number } => {
  const extractScores = (section: any): number[] => {
    if (section?.results) {
      return section.results
        .filter((item: any) => item?._hasScore && typeof item.score === 'number' && item.score !== 0)
        .map((item: any) => item.score)
    }
    return []
  }

  const readabilityScores = extractScores(data?.readability)
  const seoScores = extractScores(data?.seo?.[''])

  const allScores = [...readabilityScores, ...seoScores]

  if (allScores.length === 0) {
    return { score: 0, issueCount: 0 }
  }

  const totalScore = (allScores.reduce((sum, score) => sum + score, 0) / allScores.length) * 10
  const finalScore = Math.round(totalScore * 100) / 100

  let issueCount = 0
  allScores.forEach((score) => {
    if (score !== 0 && score < 9) {
      issueCount++
    }
  })

  return {
    score: Math.round(finalScore),
    issueCount,
  }
}

type SEOScoreGaugeContentProps = {
  error: boolean
  results: Result | null
  className?: string
}

const SEOScoreGaugeContent = ({ error, results, className }: SEOScoreGaugeContentProps): React.ReactElement => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { score, issueCount } = useMemo(() => {
    return calculateYoastTotals(results)
  }, [results])

  const point = calculateSemicirclePoint(score)

  const pointColor = useMemo(() => {
    if (score <= 30) {
      return RED
    } else if (score <= 70) {
      return YELLOW
    } else {
      return GREEN
    }
  }, [score])

  if (error) {
    return <SEOAnalysisError keyword="" />
  }

  return (
    <div className={cx(Styles.gaugeContainer, className)}>
      <svg viewBox="0 0 270 130" className={Styles.gauge}>
        <path
          className={Styles.red}
          d="M3.39691 82.9318C1.73509 82.6653 0.600035 81.1013 0.907938 79.4466C3.84256 63.6763 9.86316 48.6298 18.6346 35.1756C27.406 21.7213 38.7442 10.1415 51.9898 1.09338C53.3795 0.144038 55.2686 0.551535 56.1829 1.96457C57.0972 3.3776 56.6904 5.26091 55.3019 6.21207C42.7726 14.7951 32.0453 25.7651 23.7402 38.5041C15.4351 51.2431 9.72594 65.4846 6.92787 80.4119C6.61779 82.0661 5.05872 83.1982 3.39691 82.9318Z"
          fill={RED}
        />
        <path
          className={Styles.yellow}
          d="M0.767569 21.3389C-0.110133 19.9028 0.340878 18.0235 1.79843 17.1819C20.258 6.52405 41.2073 0.883883 62.5581 0.850769C83.909 0.817656 104.876 6.39281 123.368 16.9934C124.828 17.8304 125.285 19.7083 124.412 21.1471C123.539 22.5859 121.666 23.0405 120.205 22.2054C102.669 12.184 82.8 6.91421 62.5676 6.94559C42.3351 6.97697 22.4823 12.3084 4.97787 22.3841C3.51922 23.2237 1.64527 22.7749 0.767569 21.3389Z"
          fill={YELLOW}
        />
        <path
          className={Styles.green}
          d="M54.0986 82.9318C55.7604 82.6653 56.8954 81.1013 56.5875 79.4466C53.6541 63.6828 47.6373 48.6421 38.8716 35.192C30.106 21.7418 18.7754 10.1642 5.53811 1.11551C4.14867 0.165727 2.25947 0.572616 1.34471 1.98536C0.429953 3.3981 0.836139 5.28153 2.22432 6.23314C14.7457 14.8167 25.4658 25.7847 33.7654 38.5197C42.0651 51.2548 47.7707 65.4908 50.5676 80.4119C50.8777 82.0661 52.4368 83.1982 54.0986 82.9318Z"
          fill={GREEN}
        />
        <circle
          cx={point.x + GAUGE_X_OFFSET}
          cy={point.y + GAUGE_Y_OFFSET}
          r="7.5"
          fill={pointColor}
          stroke="white"
          strokeWidth="3"
        />
      </svg>

      <div className={Styles.scoreText}>
        <Heading level={2}>{score}</Heading>
        <div className={Styles.scoreTextContent}>
          <Text variant={isSystemRedesignEnabled ? 'secondary' : 'largeList'}>Content SEO Score</Text>
          <Text variant="metadata" color="var(--dark-40)">
            {isSystemRedesignEnabled ? `Suggested ${issueCount}` : `${issueCount} Suggested Improvements`}
          </Text>
        </div>
      </div>
      <div className={Styles.zero}>
        <Text variant="metadata" color="var(--dark-40)">
          0
        </Text>
      </div>

      <div className={Styles.hundred}>
        <Text variant="metadata" color="var(--dark-40)">
          100
        </Text>
      </div>
    </div>
  )
}

export const SEOScoreGaugeWithAnalysis = observer((): React.ReactElement => {
  const rootStore = useRootStore()
  const { analyze, error, results } = useSEOAnalysis()
  useDebouncedContentAnalysis(analyze)

  const activeStreamingJobs = rootStore.aiGeneration.activeStreamingLayers.size

  useEffect(() => {
    if (activeStreamingJobs === 0) {
      setTimeout(() => {
        analyze()
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStreamingJobs])

  if (activeStreamingJobs > 0) {
    return <></>
  }

  return <SEOScoreGaugeContent error={error} results={results} />
})

type SEOScoreGaugeProps = {
  analysisResults?: SEOScoreGaugeContentProps
  className?: string
}

export const SEOScoreGauge = ({ analysisResults, className }: SEOScoreGaugeProps): React.ReactElement => {
  const rootStore = useRootStore()
  const docChannel = rootStore.docStore?._almanac_file?.channel

  if (CONTENT_CHANNELS.includes(docChannel as TextChannelKey)) {
    if (analysisResults) {
      return <SEOScoreGaugeContent {...analysisResults} className={className} />
    }
    return <SEOScoreGaugeWithAnalysis />
  }

  return <></>
}
